import { useState } from 'react';
import {
  Badge,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { InlineEditTable } from '@duke-hq/ui/components/InlineEditTable';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { testIds } from './testIds';

interface ProviderEmailDomainsProps {
  providerId: number;
}

export function ProviderEmailDomains({ providerId }: ProviderEmailDomainsProps) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [newDomain, setNewDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingDefaultId, setPendingDefaultId] = useState<string | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const defaultDialog = useDisclosure();
  const removeDialog = useDisclosure();

  const { data: domains, isLoading } =
    trpcAdmin.providers.emailDomains.getProviderEmailDomains.useQuery({ providerId });

  const invalidateQuery = () =>
    utils.providers.emailDomains.getProviderEmailDomains.invalidate({ providerId });

  const addMutation = trpcAdmin.providers.emailDomains.addProviderEmailDomain.useMutation({
    onSuccess: () => {
      setNewDomain('');
      setValidationError(null);
      void invalidateQuery();
    },
    onError: (error) => {
      toast({
        title: t`Failed to add domain`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const removeMutation = trpcAdmin.providers.emailDomains.removeProviderEmailDomain.useMutation({
    onSuccess: () => {
      void invalidateQuery();
    },
    onError: (error) => {
      toast({
        title: t`Failed to remove domain`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const updateDefaultMutation =
    trpcAdmin.providers.emailDomains.updateDefaultProviderEmailDomain.useMutation({
      onSuccess: () => {
        void invalidateQuery();
      },
      onError: (error) => {
        toast({
          title: t`Failed to update default domain`,
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const isMutating =
    addMutation.isPending || removeMutation.isPending || updateDefaultMutation.isPending;

  const validateDomain = (domain: string): string | null => {
    const trimmed = domain.trim().toLowerCase();

    if (trimmed.length === 0) {
      return t`Domain is required`;
    }

    if (trimmed.includes('@')) {
      return t`Domain must not contain @`;
    }

    if (!/^[^.]+\.[^.]+/.test(trimmed)) {
      return t`Invalid domain format`;
    }

    if (domains?.some((d) => d.domain === trimmed)) {
      return t`This domain already exists for this provider`;
    }

    return null;
  };

  const handleAdd = () => {
    const error = validateDomain(newDomain);

    if (error != null) {
      setValidationError(error);
      return;
    }

    addMutation.mutate({ providerId, domain: newDomain.trim().toLowerCase() });
  };

  const handleSetDefault = (domainId: string) => {
    updateDefaultMutation.mutate({ domainId });
  };

  if (isLoading) {
    return (
      <FormControl>
        <FormLabel>
          <Trans>Email domains</Trans>
        </FormLabel>
        <Spinner size="sm" />
      </FormControl>
    );
  }

  const listItems = (domains ?? []).map((d) => ({ ...d, label: d.domain }));

  return (
    <FormControl data-testid={testIds.domainList}>
      <FormLabel>
        <Trans>Email domains</Trans>
      </FormLabel>

      <InlineEditTable
        items={listItems}
        inputPrefix="@"
        inputPlaceholder={`example.com.au`}
        inputValue={newDomain}
        onInputChange={(value) => {
          setNewDomain(value);
          setValidationError(null);
        }}
        onSubmit={handleAdd}
        canSubmit={validateDomain(newDomain) == null}
        isDisabled={isMutating}
        error={validationError}
        inputTestId={testIds.addDomainInput}
        renderItemLabel={(item) => (
          <span style={{ fontWeight: item.isDefault ? 500 : 400 }}>@{item.label}</span>
        )}
        renderItemActions={(item) =>
          item.isDefault ? (
            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
              <Trans>Default</Trans>
            </Badge>
          ) : (
            <Menu gutter={8}>
              <MenuButton
                as={IconButton}
                aria-label={t`Domain options`}
                icon={<Icon as={MoreHorizontal} boxSize={4} />}
                size="xs"
                variant="ghost"
                isDisabled={isMutating}
              />
              <MenuList fontSize="sm">
                <MenuItem
                  onClick={() => {
                    setPendingDefaultId(item.id);
                    defaultDialog.onOpen();
                  }}
                >
                  {t`Set as default`}
                </MenuItem>
                <MenuItem
                  icon={<Trash2 size={14} />}
                  color="red.500"
                  onClick={() => {
                    setPendingRemoveId(item.id);
                    removeDialog.onOpen();
                  }}
                >
                  {t`Remove`}
                </MenuItem>
              </MenuList>
            </Menu>
          )
        }
      />

      <FormHelperText>
        <Trans>User email addresses are restricted to these domains.</Trans>
      </FormHelperText>

      <ConfirmationDialog
        isOpen={defaultDialog.isOpen}
        onClose={() => {
          defaultDialog.onClose();
          setPendingDefaultId(null);
        }}
        title={t`Change default domain`}
        body={
          <Text>
            <Trans>
              This will change the default email domain for this provider. Are you sure?
            </Trans>
          </Text>
        }
        confirmLabel={t`Change default`}
        isLoading={updateDefaultMutation.isPending}
        onConfirm={() => {
          if (pendingDefaultId != null) {
            handleSetDefault(pendingDefaultId);
          }
          defaultDialog.onClose();
          setPendingDefaultId(null);
        }}
      />

      <ConfirmationDialog
        isOpen={removeDialog.isOpen}
        onClose={() => {
          removeDialog.onClose();
          setPendingRemoveId(null);
        }}
        title={t`Remove domain`}
        body={
          <Text>
            <Trans>
              Users with this email domain will no longer be able to be added to this provider. Are
              you sure?
            </Trans>
          </Text>
        }
        confirmLabel={t`Remove`}
        variant="destructive"
        isLoading={removeMutation.isPending}
        onConfirm={() => {
          if (pendingRemoveId != null) {
            removeMutation.mutate({ domainId: pendingRemoveId });
          }
          removeDialog.onClose();
          setPendingRemoveId(null);
        }}
      />
    </FormControl>
  );
}
